import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteScroll } from './useInfiniteScroll';
import type { PaginatedResponse } from '~/types';

/**
 * Drives an infinite-scroll list backed by a paginated React Query hook.
 * Accumulates pages into a single `data` array and automatically fetches the
 * next page when the sentinel element scrolls into view.
 *
 * @param fetchHook        - A React Query-backed hook with the signature
 *                           `(page, pageSize, filters?) => UseQueryResult`.
 *                           Must accept `page` (0-based) and `pageSize` as its
 *                           first two positional arguments.
 *
 * @param select           - Extracts `{ content, paging }` from the raw hook
 *                           result. Keeps this hook agnostic about each
 *                           endpoint's response shape.
 *
 * @param options.pageSize - Number of items per page (default: 10).
 *
 * @param options.filters  - Optional search/sort/filter params forwarded to
 *                           `fetchHook` as its third argument. Changing these
 *                           resets the list back to page 0 automatically.
 *
 * @param options.sort     - Optional `[{ field, direction }]` forwarded to `fetchHook` as its
 *                           fourth argument. Changing it resets to page 0 like `filters` does,
 *                           unless `catchUpOnSortChange` is set.
 *
 * @param options.catchUpOnSortChange
 *                         - Opt-in (default `false`). When only `sort` changes (same
 *                           `filters`), the list re-fetches page 0 in the new order and then
 *                           auto-advances pages 1..N until it has re-loaded as many pages as
 *                           were on screen before the change, or the server runs out of pages,
 *                           whichever comes first (hard cap: `MAX_CATCH_UP_PAGES`). Scroll-
 *                           triggered loading is suspended for the duration; `isLoadingMore`
 *                           and `isCatchingUp` are true throughout. A `filters` change always
 *                           collapses to a single page, catch-up or not.
 *
 * @param options.eager    - Opt-in (default `false`). Pages through the ENTIRE result set as
 *                           soon as each page lands, with no scroll or rendered `sentinelRef`
 *                           involved. For call sites that need the full result set rather than
 *                           a paged slice of it, e.g. a dedup/lookup list or rows listed ahead
 *                           of another paged list.
 *
 * @param options.enabled  - Opt-in (default `true`). Forwarded to `fetchHook` in its 5th
 *                           `PaginatedFetchOptions` argument so a caller can mount two mutually-exclusive
 *                           `usePaginatedQuery` calls unconditionally (satisfying the rules of
 *                           hooks) and toggle which one is actually fetching, instead of putting
 *                           the `usePaginatedQuery` call itself behind a condition.
 *
 * @returns
 *   - `data`          — Accumulated list of all fetched items.
 *   - `sentinelRef`   — Attach to a `<div>` at the bottom of the list to
 *                       trigger the next page load on scroll.
 *   - `loadMore`      — Advances one page on demand, for a list that offers an explicit
 *                       "show more" action instead of (or alongside) the scroll sentinel.
 *                       Guard it with `hasMoreData` and `isLoading`.
 *   - `isLoadingMore` — `true` while a subsequent page is being fetched, including during a
 *                       sort-change catch-up replay.
 *   - `isLoading`     — `true` during any loading state, including page 0 and while a
 *                       filter change awaits its first response.
 *   - `isCatchingUp`  — `true` while a sort-change replay is still rebuilding the list.
 *   - `hasMoreData`   — `true` while the server has more pages to return, `null` before
 *                       the first page returns.
 *   - `isPlaceholderData` — `true` while `keepPreviousData` is serving the previous query's rows.
 *   - `isShowingPreviousParams` — `true` while page 0 of new filters/sort is still serving the
 *                       previous params' rows as `keepPreviousData` placeholder. Unlike
 *                       `isPlaceholderData`, it stays `false` while a later page of the same
 *                       params loads.
 *   - `error`         — Query error, if any.
 *   - `refetch`       — Re-runs the currently loaded page, for a retry action on an error state.
 *   - `page`          — 0-based index of the page currently being loaded.
 *   - `pagination`    — Raw paging metadata from the last server response
 *                       (`{ number, size, totalElements, totalPages }`).
 *   - `raw`            — The last raw `fetchHook` response, for reading endpoint-specific
 *                       top-level fields that `select` doesn't surface (e.g. an aggregate
 *                       alongside `content`/`paging`).
 *   - `reset`         — Clears all accumulated items except the first page and returns to page 0.
 *                       Clearing all items created a bug that occurs in situations where an edited
 *                       value in the list is the same as before and the caching layer doesn't recognize it as an update,
 *                       thus rendering an empty list.
 *                       Call this after a mutation that invalidates the list.
 *
 * @example — no filters
 *   const { data, sentinelRef, isLoadingMore } = usePaginatedQuery({
 *     fetchHook: useGetBuyerInventories,
 *     select: (data) => data?.buyerInventory,
 *   });
 *
 * @example — with filters and custom page size
 *   const { data, sentinelRef, pagination } = usePaginatedQuery({
 *     fetchHook: useGetProducts,
 *     select: (data) => data?.products,
 *     options: { pageSize: 20, filters: { search, sortBy } },
 *   });
 */

export type RouteSort = { field: string; direction: 'ASC' | 'DESC' }[];

export type PaginatedFetchOptions = {
  enabled?: boolean;
  placeholderData?: typeof keepPreviousData;
};

// Safety valve on the sort-change replay: a user who has scrolled very deep would otherwise
// fire one sequential request per loaded page. 25 pages is ~250 rows at the default page size.
const MAX_CATCH_UP_PAGES = 25;

interface UsePaginatedQueryOptions<
  TRaw,
  TItem extends { id: string },
  TFilters,
> {
  fetchHook: (
    page: number,
    pageSize: number,
    filters?: TFilters,
    sort?: RouteSort,
    options?: PaginatedFetchOptions
  ) => {
    data: TRaw | undefined;
    isLoading: boolean;
    isSuccess: boolean;
    isRefetching: boolean;
    isPlaceholderData: boolean;
    error: Error | null;
    refetch?: () => unknown;
  };
  select: (data: TRaw | undefined) => PaginatedResponse<TItem> | undefined;
  options?: {
    pageSize?: number;
    filters?: TFilters;
    sort?: RouteSort;
    catchUpOnSortChange?: boolean;
    eager?: boolean;
    enabled?: boolean;
  };
}

export type FilterCriteria = {
  operator: string;
  value: string | number | boolean;
  field: string;
};

export function usePaginatedQuery<
  TRaw,
  TItem extends { id: string },
  TFilters = FilterCriteria[],
>({
  fetchHook,
  select,
  options,
}: UsePaginatedQueryOptions<TRaw, TItem, TFilters>) {
  const pageSize = options?.pageSize ?? 10;
  const filters = options?.filters;
  const sort = options?.sort;
  const catchUpOnSortChange = options?.catchUpOnSortChange ?? false;
  const eager = options?.eager ?? false;
  const enabled = options?.enabled ?? true;

  const [page, setPage] = useState(0);
  const [allItems, setAllItems] = useState<TItem[]>([]);
  // Page index the sort-change catch-up is replaying toward; null while idle.
  const [catchUpToPage, setCatchUpToPage] = useState<number | null>(null);

  const filtersKey = useMemo(() => JSON.stringify(filters ?? null), [filters]);
  const sortKey = useMemo(() => JSON.stringify(sort ?? null), [sort]);
  const queryParamsKey = `${filtersKey}|${sortKey}`;

  const lastProcessedParamsKeyRef = useRef(queryParamsKey);
  // Set by reset(), cleared only when a page-0 response is processed, so an in-flight page-1
  // response landing first can't consume the replace-on-reset intent.
  const pendingResetRef = useRef(false);
  // Page number of the most recently merged response, so the catch-up effect advances at most
  // one page per merged response rather than racing ahead of what's actually landed.
  const lastMergedPageRef = useRef<number | null>(null);

  const [renderedKeys, setRenderedKeys] = useState({ filtersKey, sortKey });
  // Intentional render-phase reset (react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes),
  // not a stray setState-during-render bug: React re-runs this function synchronously
  // before committing, so `page` below already reflects the reset by the time it's read.
  if (
    filtersKey !== renderedKeys.filtersKey ||
    sortKey !== renderedKeys.sortKey
  ) {
    // A sort-only change keeps the same result set, just reordered, so it's worth replaying the
    // rows already scrolled to. A filters change is a different result set, so it always
    // collapses to one page, catch-up or not.
    const isSortOnlyChange = filtersKey === renderedKeys.filtersKey;
    setRenderedKeys({ filtersKey, sortKey });
    setPage(0);
    if (catchUpOnSortChange && isSortOnlyChange) {
      const loadedPages = Math.ceil(allItems.length / pageSize);
      // max() with the in-flight target: a second sort click mid-catch-up must not shrink the
      // goal down to whatever the first click had managed to reload so far.
      const target = Math.max(loadedPages - 1, catchUpToPage ?? 0, 0);
      setCatchUpToPage(Math.min(target, MAX_CATCH_UP_PAGES - 1));
    } else {
      setCatchUpToPage(null);
    }
  }

  const isCatchingUp = catchUpToPage !== null && page <= catchUpToPage;

  const rawResult = fetchHook(page, pageSize, filters, sort, {
    enabled,
    // Page 0 shows the loading state, not the previous filter's rows; later pages keep the
    // loaded rows so the list keeps its scroll position.
    placeholderData: page > 0 ? keepPreviousData : undefined,
  });
  const paginatedData = select(rawResult.data);

  // isPlaceholderData is true only while keepPreviousData is serving a prior filter's
  // response as a stand-in for the new one; normal page-advance placeholder windows
  // (allItems already populated) don't hit the allItems.length === 0 branch.
  const isLoading =
    rawResult.isLoading ||
    (allItems.length === 0 && rawResult.isPlaceholderData);

  const firstPageLoaded =
    allItems.length > 0 || paginatedData?.paging.totalElements === 0;

  let hasMoreData: boolean | null = null;
  // isCatchingUp is forced in: on the commit where a sort change's page-0 response replaces
  // allItems, allItems still holds the pre-sort array, so allItems.length can already equal
  // totalElements and flip this false mid-replay, which would surface a premature "no more
  // items" message and stop the replay's own scroll-independent advance from being trusted.
  hasMoreData = rawResult.isSuccess
    ? isCatchingUp ||
      allItems.length < (paginatedData?.paging.totalElements || 0)
    : null;

  const onLoadMore = useCallback(() => setPage((p) => p + 1), []);

  const { sentinelRef, reset: resetIsLoadingMore } = useInfiniteScroll({
    hasMore: eager ? false : hasMoreData,
    // isCatchingUp suppresses the scroll observer for the whole replay: a scroll-triggered
    // onLoadMore landing in the same batch as the catch-up's own setPage would advance two
    // pages at once and leave a permanent hole in the list.
    isLoading: isLoading || isCatchingUp || !firstPageLoaded,
    onLoadMore,
  });

  const reset = () => {
    setPage(0);
    setCatchUpToPage(null);
    setAllItems((prev) => prev.slice(0, pageSize));
    // The next page-0 response replaces the list instead of merging, so a row an edit moved to a
    // later page can't linger here with its pre-edit values.
    pendingResetRef.current = true;
  };

  useEffect(() => {
    // queryParamsKey is a dep, so this fires on a filters/sort change before the real response
    // arrives, while content is still keepPreviousData's stale placeholder for the OLD params.
    // Processing that would consume isFirstResponseForNewParams early, so the real response
    // landing right after gets merged with the stale one instead of replacing it — e.g. showing
    // both an old and new length's rows for the same seller.
    if (rawResult.isPlaceholderData) return;
    const incoming = paginatedData?.content;
    if (incoming === undefined || incoming === null) {
      // A failed page stays latched: a retry serves the previous page as placeholder, so an
      // unlatched sentinel fires during the retry and skips a page.
      if (!rawResult.error) resetIsLoadingMore();
      return;
    }
    const nonNullIncoming = incoming.filter((item) => item !== null);

    const responsePage = paginatedData?.paging.number ?? page;
    // Replaces allItems wholesale on the first page-0 response after a filters/sort change,
    // instead of clearing eagerly on the change itself — an eager clear raced ahead of
    // keepPreviousData's stale-data placeholder and flashed an empty state on every
    // keystroke of a live search.
    const isFirstResponseForNewParams =
      queryParamsKey !== lastProcessedParamsKeyRef.current;
    const replaceOnPageZero =
      isFirstResponseForNewParams || pendingResetRef.current;
    const clearItems = () => setAllItems([]);
    if (nonNullIncoming.length === 0) {
      if (responsePage === 0) {
        clearItems();
        pendingResetRef.current = false;
      }
      lastProcessedParamsKeyRef.current = queryParamsKey;
      lastMergedPageRef.current = responsePage;
      resetIsLoadingMore();
      return;
    }

    const appendItems = () =>
      setAllItems((prev) => {
        const incomingById = new Map(
          nonNullIncoming.map((item) => [item.id, item])
        );

        if (responsePage === 0) {
          if (replaceOnPageZero) {
            return nonNullIncoming;
          }
          const incomingIds = new Set(incomingById.keys());
          const preserved = prev.filter((item) => !incomingIds.has(item.id));
          return [...nonNullIncoming, ...preserved];
        }
        const updated = prev.map((item) => incomingById.get(item.id) ?? item);
        const prevIds = new Set(prev.map((item) => item.id));
        const brandNew = nonNullIncoming.filter(
          (item) => !prevIds.has(item.id)
        );
        return [...updated, ...brandNew];
      });

    appendItems();
    if (responsePage === 0) pendingResetRef.current = false;
    lastProcessedParamsKeyRef.current = queryParamsKey;
    lastMergedPageRef.current = responsePage;
    resetIsLoadingMore();
  }, [
    paginatedData?.content,
    paginatedData?.paging.number,
    page,
    resetIsLoadingMore,
    queryParamsKey,
    rawResult.isPlaceholderData,
    rawResult.error,
  ]);

  // Auto-replays pages 1..N after a sort-only change so the list returns to the depth the user
  // had scrolled to, in the new order. Declared after the merge effect above so that within a
  // single commit the merge has already updated the refs this reads.
  useEffect(() => {
    if (catchUpToPage === null) return;
    // A failed page would otherwise strand isCatchingUp true and disable the sentinel forever.
    if (rawResult.error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- aborts the replay in response to the query's own error, terminating rather than cascading further
      setCatchUpToPage(null);
      return;
    }
    if (rawResult.isPlaceholderData) return;
    // Advance only off a response that belongs to the current params AND the current page —
    // otherwise this would burst ahead of what's actually landed.
    if (lastProcessedParamsKeyRef.current !== queryParamsKey) return;
    if (lastMergedPageRef.current !== page) return;

    // Clamped to what the server actually has now (not hasMoreData, which is forced true
    // during a replay): the dataset may have shrunk since the rows were first loaded.
    const lastServerPage = (paginatedData?.paging.totalPages ?? 0) - 1;
    const target = Math.min(catchUpToPage, lastServerPage);
    if (page >= target) {
      setCatchUpToPage(null);
      return;
    }
    setPage(page + 1);
  }, [
    catchUpToPage,
    page,
    queryParamsKey,
    paginatedData?.paging.totalPages,
    paginatedData?.paging.number,
    paginatedData?.content,
    rawResult.isPlaceholderData,
    rawResult.error,
  ]);

  useEffect(() => {
    if (!eager) return;
    if (rawResult.error) return;
    if (rawResult.isPlaceholderData) return;
    if (lastProcessedParamsKeyRef.current !== queryParamsKey) return;
    if (lastMergedPageRef.current !== page) return;
    const totalPages = paginatedData?.paging.totalPages ?? 0;
    if (page + 1 >= totalPages) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors the catch-up effect's own scroll-independent page advance above
    setPage(page + 1);
  }, [
    eager,
    page,
    queryParamsKey,
    paginatedData?.paging.totalPages,
    paginatedData?.paging.number,
    paginatedData?.content,
    rawResult.isPlaceholderData,
    rawResult.error,
  ]);

  const updateItem = useCallback(
    (id: string, updater: (item: TItem) => TItem) => {
      setAllItems((prev) =>
        prev.map((item) => (item.id === id ? updater(item) : item))
      );
    },
    []
  );

  // A page-0 refetch preserves accumulated items absent from the incoming page, so a deleted item
  // would linger until a filter change. Callers drop it from the accumulated list on delete.
  const removeItem = useCallback((id: string) => {
    setAllItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    data: allItems,
    sentinelRef,
    loadMore: onLoadMore,
    isLoadingMore: rawResult.isRefetching || isCatchingUp,
    isLoading,
    isCatchingUp,
    hasMoreData,
    isPlaceholderData: rawResult.isPlaceholderData,
    isShowingPreviousParams: rawResult.isPlaceholderData && page === 0,
    error: rawResult.error,
    refetch: rawResult.refetch ?? (() => {}),
    page,
    pagination: paginatedData?.paging,
    raw: rawResult.data,
    reset,
    updateItem,
    removeItem,
  };
}
